import { Box, CircularProgress } from '@mui/material'
import { lazy, Suspense } from 'react'
import { Route, Routes } from 'react-router-dom'
import { API_TOKEN_CALLBACK_PATH } from './api/apiTokens/offlineToken'
import { LanguageInitializer } from './components/LanguageInitializer'
import { PaymentRedirect } from './components/PaymentRedirect'
import { ProtectedRoute } from './components/ProtectedRoute/ProtectedRoute'
import { WebViewLayout } from './components/WebViewLayout'
import { WebViewRoute } from './components/WebViewRoute'
import {
    ASSISTANT_SHOP_ROUTE,
    ASSISTANT_USAGE_AGREEMENT_ROUTE,
    CALENDAR_ROUTE,
    DISK_ROUTE,
    FINANCES_ROUTE,
    FORGOT_PASSWORD_ROUTE,
    LOGIN_ROUTE,
    MEETS_ROUTE,
    NOTIFICATIONS_ROUTE,
    PERSONAL_DATA_AGREEMENT_ROUTE,
    PROFILE_ROUTE,
    PROJECT_ROUTE,
    RECORD_ROUTE,
    RECORDS_ROUTE,
    REFERRAL_PROGRAM_ROUTE,
    REQUEST_ROUTE,
    REQUESTS_ROUTE,
    ROOT_ROUTE,
    SETTING_ROUTE,
    SHARE_ROUTE,
    SIGN_UP_ROUTE,
    TERMS_OF_USE_ROUTE,
    USER_AGREEMENT_ROUTE,
    WEB_VIEW_ROUTE,
} from './constants/routing.constants'
import { Providers } from './providers/Providers'
import { PaymentStatus } from './types/billing'

// Lazy load MainLayout to avoid loading Wrapper and all internal components on LoginPage
const MainLayout = lazy(() =>
    import('./components/MainLayout').then((m) => ({ default: m.MainLayout })),
)

// Loading component
const PageLoader = () => (
    <Box
        sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100vh',
            width: '100vw',
        }}
    >
        <CircularProgress />
    </Box>
)

// External Pages - Load immediately (critical)
const LoginPage = lazy(() =>
    import('./pages/ExternalPage/LoginPage/LoginPage').then((m) => ({ default: m.LoginPage })),
)
const MemoStenographySharePage = lazy(() =>
    import('./pages/ExternalPage/MemoStenographySharePage/MemoStenographySharePage').then((m) => ({
        default: m.MemoStenographySharePage,
    })),
)
const SharedThreadPage = lazy(
    () => import('./pages/ExternalPage/SharedThreadPage/SharedThreadPage'),
)
const ApiTokenSilentCallback = lazy(() =>
    import('./pages/InternalPage/ProfilePage/components/ApiTokens/ApiTokenSilentCallback').then(
        (m) => ({ default: m.ApiTokenSilentCallback }),
    ),
)

// Internal Pages - Main (high priority)
const MainPage = lazy(() =>
    import('./pages/InternalPage/MainPage/MainPage').then((m) => ({ default: m.MainPage })),
)
const ErrorPage = lazy(() =>
    import('./pages/InternalPage/ErrorPage/ErrorPage').then((m) => ({ default: m.ErrorPage })),
)

// Internal Pages - Requests (high priority)
const RequestPage = lazy(() =>
    import('./pages/InternalPage/RequestsPage/RequestPage').then((m) => ({
        default: m.RequestPage,
    })),
)
const RequestMessagesPage = lazy(() =>
    import('./pages/InternalPage/RequestMessagesPage/RequestMessagesPage').then((m) => ({
        default: m.RequestMessagesPage,
    })),
)
const RequestSettingPage = lazy(() =>
    import('./pages/InternalPage/RequestSettingPage/RequestSettingPage').then((m) => ({
        default: m.RequestSettingPage,
    })),
)

// Internal Pages - Projects
const ProjectPage = lazy(() =>
    import('./pages/InternalPage/ProjectPage/ProjectPage').then((m) => ({
        default: m.ProjectPage,
    })),
)
const ProjectArchivePage = lazy(() =>
    import('./pages/InternalPage/ProjectArchivePage/ProjectArchivePage').then((m) => ({
        default: m.ProjectArchivePage,
    })),
)

// Internal Pages - Records
const RecordPage = lazy(() =>
    import('./pages/InternalPage/RecordsPage/RecordPage').then((m) => ({ default: m.RecordPage })),
)
const RecordUploadPage = lazy(() =>
    import('./pages/InternalPage/RecordUploadPage/RecordUploadPage').then((m) => ({
        default: m.RecordUploadPage,
    })),
)
const RecordDetailPage = lazy(() =>
    import('./pages/InternalPage/RecordDetailPage/RecordDetailPage').then((m) => ({
        default: m.RecordDetailPage,
    })),
)

// Internal Pages - Settings & Profile
const ModelSetupPage = lazy(() =>
    import('./pages/InternalPage/ModelSetupPage/ModelSetupPage').then((m) => ({
        default: m.ModelSetupPage,
    })),
)
const ProfilePage = lazy(() =>
    import('./pages/InternalPage/ProfilePage/ProfilePage').then((m) => ({
        default: m.ProfilePage,
    })),
)

// Internal Pages - Calendar & Meets
const MeetsPage = lazy(() =>
    import('./pages/InternalPage/MeetPage/MeetsPage').then((m) => ({ default: m.MeetsPage })),
)
const CalendarPage = lazy(() =>
    import('./pages/InternalPage/CalendarPage/CalendarPage').then((m) => ({
        default: m.CalendarPage,
    })),
)

// Internal Pages - Storage
const DiskPage = lazy(() =>
    import('./pages/InternalPage/DiskPage/DiskPage').then((m) => ({ default: m.DiskPage })),
)

// Internal Pages - Finances & Shop
const FinancesPage = lazy(() =>
    import('./pages/InternalPage/FinancesPage/FinancesPage').then((m) => ({
        default: m.FinancesPage,
    })),
)
const AssistantShopPage = lazy(() =>
    import('./pages/InternalPage/AssistantShopPage/AssistantShopPage').then((m) => ({
        default: m.AssistantShopPage,
    })),
)
const ReferralProgramPage = lazy(
    () => import('./pages/InternalPage/ReferralProgramPage/ReferralProgramPage'),
)

// Internal Pages - Legal & Notifications
const TermsOfUsePage = lazy(() =>
    import('./pages/InternalPage/TermsOfUsePage/TermsOfUsePage').then((m) => ({
        default: m.TermsOfUsePage,
    })),
)
const UserAgreementPage = lazy(() =>
    import('./pages/InternalPage/UserAgreementPage/UserAgreementPage').then((m) => ({
        default: m.UserAgreementPage,
    })),
)
const AssistantsUsageAgreementPage = lazy(() =>
    import('./pages/InternalPage/AssistantsUsageAgreementPage/AssistantsUsageAgreementPage').then(
        (m) => ({ default: m.AssistantsUsageAgreementPage }),
    ),
)
const PersonalDataAgreementPage = lazy(() =>
    import('./pages/InternalPage/PersonalDataAgreementPage/PersonalDataAgreementPage').then(
        (m) => ({ default: m.PersonalDataAgreementPage }),
    ),
)
const NotificationsPage = lazy(() =>
    import('./pages/InternalPage/NotificationsPage/NotificationsPage').then((m) => ({
        default: m.NotificationsPage,
    })),
)

// WebView Pages
const WebViewFinancesPage = lazy(() =>
    import('./pages/WebViewPage/FinancesPage').then((m) => ({ default: m.FinancesPage })),
)
const WebViewAssistantPayPage = lazy(() =>
    import('./pages/WebViewPage/AssistantPayPage').then((m) => ({ default: m.AssistantPayPage })),
)

export default function App() {
    // Hidden silent-auth iframe lands here with ?code=...&state=... issued by the
    // arhiassistant-api OIDC client. We short-circuit BEFORE <Providers> so the
    // main app's AuthProvider (react-oidc-context for arhiassistant-app) never
    // runs in the iframe — otherwise it would try to consume that code/state as
    // its own signin response and reject with "No matching state found in
    // storage" (the state lives in the api-client's InMemoryWebStorage, not in
    // sessionStorage where AuthProvider looks). Bonus: no Theme/Router/Query
    // mount in the iframe.
    if (window.location.pathname === API_TOKEN_CALLBACK_PATH) {
        return (
            <Suspense fallback={null}>
                <ApiTokenSilentCallback />
            </Suspense>
        )
    }
    return (
        <Providers>
            <LanguageInitializer>
                <Suspense fallback={<PageLoader />}>
                    <Routes>
                        <Route path={LOGIN_ROUTE} element={<LoginPage />} />
                        <Route path={SIGN_UP_ROUTE} element={<LoginPage />} />
                        <Route path={FORGOT_PASSWORD_ROUTE} element={<LoginPage />} />
                        <Route
                            path={`${SHARE_ROUTE}/summary/`}
                            element={<MemoStenographySharePage />}
                        />
                        <Route path={`${SHARE_ROUTE}/thread/`} element={<SharedThreadPage />} />

                        <Route
                            element={
                                <ProtectedRoute>
                                    <Suspense fallback={<PageLoader />}>
                                        <MainLayout />
                                    </Suspense>
                                </ProtectedRoute>
                            }
                        >
                            <Route
                                path={ROOT_ROUTE}
                                element={
                                    <ProtectedRoute>
                                        <MainPage />
                                    </ProtectedRoute>
                                }
                            />

                            <Route
                                path={REQUESTS_ROUTE}
                                element={
                                    <ProtectedRoute>
                                        <RequestPage />
                                    </ProtectedRoute>
                                }
                            />

                            <Route
                                path={`${REQUEST_ROUTE}/:requestId`}
                                element={
                                    <ProtectedRoute>
                                        <RequestMessagesPage />
                                    </ProtectedRoute>
                                }
                            />

                            <Route path={PROJECT_ROUTE}>
                                <Route
                                    index
                                    element={
                                        <ProtectedRoute>
                                            <ProjectPage />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path=":projectId/setting"
                                    element={
                                        <ProtectedRoute>
                                            <RequestSettingPage type="project" />
                                        </ProtectedRoute>
                                    }
                                />

                                <Route
                                    path="archive/:workplaceId"
                                    element={
                                        <ProtectedRoute>
                                            <ProjectPage isWorkplaceArchived={true} />
                                        </ProtectedRoute>
                                    }
                                />

                                <Route
                                    path="archive"
                                    element={
                                        <ProtectedRoute>
                                            <ProjectArchivePage />
                                        </ProtectedRoute>
                                    }
                                />
                            </Route>

                            <Route path={RECORDS_ROUTE}>
                                <Route
                                    index
                                    element={
                                        <ProtectedRoute>
                                            <RecordPage />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="upload"
                                    element={
                                        <ProtectedRoute>
                                            <RecordUploadPage />
                                        </ProtectedRoute>
                                    }
                                />
                            </Route>

                            <Route
                                path={`${RECORD_ROUTE}/:recordId`}
                                element={
                                    <ProtectedRoute>
                                        <RecordDetailPage recordId={':recordId'} />
                                    </ProtectedRoute>
                                }
                            />

                            <Route
                                path={SETTING_ROUTE}
                                element={
                                    <ProtectedRoute>
                                        <ModelSetupPage />
                                    </ProtectedRoute>
                                }
                            />

                            <Route
                                path={MEETS_ROUTE}
                                element={
                                    <ProtectedRoute>
                                        <MeetsPage />
                                    </ProtectedRoute>
                                }
                            />

                            <Route
                                path={CALENDAR_ROUTE}
                                element={
                                    <ProtectedRoute>
                                        <CalendarPage />
                                    </ProtectedRoute>
                                }
                            />

                            <Route
                                path={DISK_ROUTE}
                                element={
                                    <ProtectedRoute>
                                        <DiskPage />
                                    </ProtectedRoute>
                                }
                            />

                            <Route path={FINANCES_ROUTE}>
                                <Route
                                    index
                                    element={
                                        <ProtectedRoute>
                                            <FinancesPage />
                                        </ProtectedRoute>
                                    }
                                />
                            </Route>

                            <Route
                                path={PROFILE_ROUTE}
                                element={
                                    <ProtectedRoute>
                                        <ProfilePage />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path={ASSISTANT_SHOP_ROUTE}
                                element={
                                    <ProtectedRoute>
                                        <AssistantShopPage />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path={REFERRAL_PROGRAM_ROUTE}
                                element={
                                    <ProtectedRoute>
                                        <ReferralProgramPage />
                                    </ProtectedRoute>
                                }
                            />

                            <Route path={TERMS_OF_USE_ROUTE}>
                                <Route
                                    index
                                    element={
                                        <ProtectedRoute>
                                            <TermsOfUsePage />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path={USER_AGREEMENT_ROUTE}
                                    element={
                                        <ProtectedRoute>
                                            <UserAgreementPage />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path={ASSISTANT_USAGE_AGREEMENT_ROUTE}
                                    element={
                                        <ProtectedRoute>
                                            <AssistantsUsageAgreementPage />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path={PERSONAL_DATA_AGREEMENT_ROUTE}
                                    element={
                                        <ProtectedRoute>
                                            <PersonalDataAgreementPage />
                                        </ProtectedRoute>
                                    }
                                />
                            </Route>

                            <Route
                                path={NOTIFICATIONS_ROUTE}
                                element={
                                    <ProtectedRoute>
                                        <NotificationsPage />
                                    </ProtectedRoute>
                                }
                            />
                        </Route>
                        <Route
                            path={`${FINANCES_ROUTE}/isPaymentSuccessful`}
                            element={<PaymentRedirect status={PaymentStatus.SUCCESS} />}
                        />
                        <Route
                            path={`${FINANCES_ROUTE}/isPaymentFailed`}
                            element={<PaymentRedirect status={PaymentStatus.ERROR} />}
                        />
                        <Route
                            path={WEB_VIEW_ROUTE}
                            element={
                                <WebViewRoute>
                                    <WebViewLayout />
                                </WebViewRoute>
                            }
                        >
                            <Route
                                path={`${WEB_VIEW_ROUTE}${FINANCES_ROUTE}`}
                                element={<WebViewFinancesPage />}
                            />
                            <Route
                                path={`${WEB_VIEW_ROUTE}/assistant-pay`}
                                element={<WebViewAssistantPayPage />}
                            />
                        </Route>

                        <Route path="*" element={<ErrorPage />} />
                    </Routes>
                </Suspense>
            </LanguageInitializer>
        </Providers>
    )
}
